import { ChangeDetectionStrategy, Component, input, output, signal } from "@angular/core";
import { MatFormField, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";

/**
 * Generic free-text input with an "add" button and a cap counter. The caller
 * drives the state (current count, max, placeholder) and reacts to the
 * `itemAdded` output to grow its own list.
 */
@Component({
  selector: "lib-object-input",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatFormField, MatInput, MatSuffix, MatIconButton, MatIcon],
  templateUrl: "./object-autocomplete.component.html",
})
export class ObjectInputComponent {
  /** Current number of items already added — drives the cap counter. */
  readonly count = input(0);

  /** Maximum allowed items. When `count >= maxItems` the input is disabled. */
  readonly maxItems = input(Number.POSITIVE_INFINITY);

  /** Placeholder shown when the input is empty. The caller provides it so
   *  this component stays domain-agnostic. */
  readonly placeholder = input("");

  /** Forces disabled state regardless of cap. */
  readonly disabled = input(false);

  /** ARIA label for the input. */
  readonly ariaLabel = input("Add item");

  /** Emits the trimmed item name when the user presses Enter or clicks add. */
  readonly itemAdded = output<string>();

  protected readonly inputValue = signal("");

  protected get atCap(): boolean {
    return this.count() >= this.maxItems();
  }

  protected handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      this.tryAdd();
    }
  }

  protected tryAdd(): void {
    const name = this.inputValue().trim();
    if (!name || this.atCap || this.disabled()) return;
    this.itemAdded.emit(name);
    this.inputValue.set("");
  }
}